import { Component, inject, TemplateRef, viewChild, computed, ViewEncapsulation } from '@angular/core';
import { PageEvent, MatPaginatorModule } from '@angular/material/paginator';
import { AppState } from 'src/app/app.state';
import { PriceRegulationListStateService } from '../services/price-regulation-list.state.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CardComponent, ExpandListComponent, ExpandListConfiguration, ScreenSizeEnum } from 'ok-angular-shared/components';
import { PriceRegulation, PriceRegulationScopeType, PriceRegulationType } from '../price-regulation';
import { DkCurrencyPipe } from 'src/app/shared/pipes/dk-currency';
import { TranslatePriceRegulationTypePipe } from 'src/app/shared/pipes/translate-price-regulation-type.pipe';
import { RouterLink } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import { PriceRegulationService } from 'src/app/shared/services/price-regulation.service';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { SnackbarService } from 'ok-angular-shared/components';
import { NoteCreationModalComponent } from 'src/app/shared/components/modals/price-policy-note-creation-modal/note-creation-modal.component';
import {
  PriceRegulationDialogCloseResult,
  PriceRegulationsCreateEditModalComponent,
} from '../price-regulations-create-edit-modal/price-regulations-create-edit-modal.component';
import { take, tap } from 'rxjs';

@Component({
  selector: 'adm-price-regulations-list',
  templateUrl: './price-regulations-list.component.html',
  styleUrls: ['./price-regulations-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    CardComponent,
    ExpandListComponent,
    MatPaginatorModule,
    MatTooltipModule,
    DkCurrencyPipe,
    RouterLink,
    TranslatePriceRegulationTypePipe,
  ],
})
export class PriceRegulationsListComponent {
  private readonly deletePriceRegulationHasLocationCode = 906003;

  state = inject(PriceRegulationListStateService);
  appState = inject(AppState);
  private dialog = inject(MatDialog);
  private priceRegulationService = inject(PriceRegulationService);
  private snackbarService = inject(SnackbarService);
  public PriceRegulationScopeType = PriceRegulationScopeType;
  public PriceRegulationType = PriceRegulationType;

  public rowTypeTemplate = viewChild<TemplateRef<unknown>>('rowTypeTemplate');
  public rowNameTemplate = viewChild<TemplateRef<unknown>>('rowNameTemplate');
  public rowAffiliationTemplate = viewChild<TemplateRef<unknown>>('rowAffiliationTemplate');
  public rowRegulationTemplate = viewChild<TemplateRef<unknown>>('rowRegulationTemplate');
  public rowLocationsTemplate = viewChild<TemplateRef<unknown>>('rowLocationsTemplate');

  public headerRegulationTemplate = viewChild<TemplateRef<unknown>>('headerRegulationTemplate');
  public headerLocationsTemplate = viewChild<TemplateRef<unknown>>('headerLocationsTemplate');

  public config = computed<ExpandListConfiguration<PriceRegulation>>(() => ({
    expandDisabled: true,
    columns: [
      { field: 'name', header: 'Navn', width: '2fr', sortField: 'name' },
      {
        field: 'type',
        header: 'Type',
        width: '1fr',
        sortField: 'type',
        displayTemplate: this.rowTypeTemplate()!,
        visibleFrom: ScreenSizeEnum.lg,
      },
      {
        field: 'organizationName',
        header: 'Tilknytning',
        width: '2fr',
        sortField: 'organization_name',
        displayTemplate: this.rowAffiliationTemplate()!,
      },
      {
        field: 'discountAmountInKrWithoutVat',
        header: this.headerRegulationTemplate()!,
        width: '1fr',
        sortField: 'regulation_value',
        displayTemplate: this.rowRegulationTemplate()!,
      },
      {
        field: 'locationCount',
        header: this.headerLocationsTemplate()!,
        width: '160px',
        sortField: 'location_count',
        visibleFrom: ScreenSizeEnum.md,
        displayTemplate: this.rowLocationsTemplate()!,
      },
    ],
    classFn: (item: PriceRegulation) => (item.highlight?.getValue() ? 'highlight' : ''),
    actions: [
      {
        icon: { name: 'edit-s' },
        label: 'Rediger prisregulering',
        action: (item?: PriceRegulation) => item && this.openEditPriceRegulation(item),
      },
      {
        icon: { name: 'trash-s' },
        label: 'Slet prisregulering',
        action: (item?: PriceRegulation) => item && this.deletePriceRegulation(item),
      },
    ],
  }));

  public pageSwitch(event: PageEvent) {
    this.state.filters.update(x => ({
      ...x,
      page: event,
    }));
  }

  private openEditPriceRegulation(priceRegulation: PriceRegulation) {
    const dialogRef = this.dialog.open(PriceRegulationsCreateEditModalComponent, {
      data: {
        identifier: priceRegulation.identifier,
      },
      disableClose: true,
      width: '650px',
    });

    dialogRef
      .afterClosed()
      .pipe(
        take(1),
        tap((result: PriceRegulationDialogCloseResult | undefined) => {
          if (result?.saved) {
            this.state.triggerUpdateWithHighlight(result.identifier);
          }
        })
      )
      .subscribe();
  }

  private deletePriceRegulation(priceRegulation: PriceRegulation) {
    const confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        icon: { name: 'trash-s' },
        title: 'Slet prisregulering',
        text: `Er du sikker på at du vil slette prisreguleringen <strong>${priceRegulation.name}</strong>?`,
        confirmText: 'Ja',
        denyText: 'Nej',
      },
      disableClose: true,
    });

    confirmDialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        const noteDialogRef = this.dialog.open(NoteCreationModalComponent, {
          data: {
            title: 'Tilføj note omkring sletning',
            confirmText: 'Gem',
            denyText: 'Annuller',
            icon: { name: 'trash-s' },
          },
        });

        noteDialogRef.afterClosed().subscribe(note => {
          if (!note?.trim?.()) {
            return;
          }

          this.priceRegulationService.deletePriceRegulation(priceRegulation.identifier, note).subscribe({
            next: () => {
              this.state.triggerUpdate();
            },
            error: (err: HttpErrorResponse) => {
              if (err.status === HttpStatusCode.BadRequest && err.error?.code === this.deletePriceRegulationHasLocationCode) {
                this.snackbarService.error('Prisreguleringen har lokationer tilknyttet og kan derfor ikke slettes.');
                return;
              }

              if (err.status === HttpStatusCode.NotFound) {
                this.snackbarService.error('Prisreguleringen kunne ikke findes.');
                return;
              }

              this.snackbarService.error(`Der skete fejl under sletning (${err.error?.code ?? err.status})`);
            },
          });
        });
      }
    });
  }
}
